'use strict';

/**
 * T-cert sealed SAVE — media-editor vault seal agents (the T538 USER-ACTION:
 * "the browser save-sealed round-trip needs a scoped master-key seal method
 * exposed on window.OysterVault" — PLAN-MEDIA-EDITOR-SUBSTRATE-V1.md).
 *
 * CUSTODY (CLAUDE.md Legal Foundation / HARDEN-6): editor documents and
 * accepted genai assets are 🔴 user CONTENT. They seal CLIENT-SIDE under the
 * USER's master key (the unlocked vault session's _identityMaster) via the
 * canonical encryptUnderMasterKey primitive — NEVER SESSION_SECRET, never a
 * server-held secret, and never the hybrid mail tag (the P2 store rejects
 * MAIL_HYBRID_V1 store-locally, F-IDOR-1). The server stores/serves the
 * envelope VERBATIM (shared/media-editor/server/routes.js, keyless-412).
 *
 * SCHEME LOCK: doc seals use the IM-FIL scheme EXACTLY — recordType
 * 'media-editor-oyproj', recordId safeRef(docId) — so envelopes interoperate
 * with shared/media-editor/image/file-io.js sealProject/openProject
 * (cross-compat pinned by __tests__/media-editor/vault-seal-agent-tcert).
 * Asset seals use the T538 P2 store convention 'media-editor-asset'.
 *
 * KEYLESS CONTRACT: no unlocked session ⇒ typed ERR_MASTER_KEY_REQUIRED
 * throw — the client NEVER produces (or PUTs) a plaintext record; the P2
 * route's 412 is the server backstop, this is the client pole.
 *
 * Seals are SYNCHRONOUS (plain envelope return) — control-entry saveSealed
 * passes the seal product straight into the PUT body.
 */

const { encryptUnderMasterKey, decryptUnderMasterKey } = require('./master-key-encryption.js');

// Colon is the HKDF info-label separator → forbidden in recordId
// (file-io safeRef precedent — kept byte-identical for scheme compat).
function safeRef(s) { return String(s).replace(/:/g, '-'); }

// Locked scheme constants — changing either silently orphans shipped envelopes.
const DOC_RECORD_TYPE = 'media-editor-oyproj';   // IM-FIL (image/file-io.js) scheme
const ASSET_RECORD_TYPE = 'media-editor-asset';  // T538 P2 sealed asset store convention

// T1075 P1 (plan LD-2) — the Audio Editor's account project doc: ONE new
// locked recordType beside the two above; DAW audio packs REUSE the asset
// convention (per-record DEK via recordId) — never a new crypto path. MUST
// equal AUDIOPROJ_RECORD_TYPE in shared/media-editor/daw/ui/io/
// account-contract.js — a mismatch derives a different DEK and silently
// orphans every saved project; __tests__/daw/t1075-p1-account-store.test.js
// pins the two constants together and round-trips a real envelope.
const AUDIOPROJ_RECORD_TYPE = 'media-editor-audioproj';

// T678 P3 — the website-editor site-doc, sealed by the SAME agent because the
// key it needs is the same one: the unlocked vault session's _identityMaster,
// reachable only through this resolver (including the T546 cross-iframe host
// bridge). The browser bundles that carry editor UI cannot reach that key on
// their own, which is why sealing is delegated here rather than duplicated.
//
// This string is an HKDF info input. It MUST equal `RECORD_TYPE` in
// shared/website-editor/site-doc-store.js — a mismatch derives a different DEK
// and silently orphans every record already written, with no error at either
// end. `__tests__/website-editor/T678-P3-browser-delivery.test.js` pins the two
// constants together and round-trips a real envelope between them.
const SITE_DOC_RECORD_TYPE = 'website-site-doc';

function keylessError() {
  const err = new Error(
    'ERR_MASTER_KEY_REQUIRED: no unlocked vault session (master key) — sealed media-editor save needs a signed-in, unlocked vault',
  );
  err.code = 'ERR_MASTER_KEY_REQUIRED';
  return err;
}

/**
 * @param {object} opts
 * @param {function(): (object|null)} opts.resolveSession — returns the live
 *   vault session ({ _identityMaster: 32-byte key }) or null when locked.
 *   The browser entry injects the T546 local→same-origin-host resolver.
 */
function createMediaEditorSealAgent({ resolveSession } = {}) {
  function masterKey() {
    let session = null;
    try { session = typeof resolveSession === 'function' ? resolveSession() : null; } catch (_e) { session = null; }
    const key = session && session._identityMaster;
    if (!key || key.length !== 32) throw keylessError();
    return key;
  }

  function seal(recordType, recordId, plaintext) {
    return encryptUnderMasterKey({
      plaintext,
      userMasterKey: masterKey(),
      recordType,
      recordId: safeRef(recordId),
    });
  }

  // Unseal binds the envelope to the REQUESTED record: a verbatim-store server
  // cannot swap another of the owner's records in undetected. Wrong key /
  // tampered ct fail-close to null (decryptUnderMasterKey is non-throwing).
  function unseal(recordType, recordId, envelope) {
    const key = masterKey();
    if (!envelope || typeof envelope !== 'object') return null;
    if (envelope.recordType !== recordType) return null;
    if (envelope.recordId !== safeRef(recordId)) return null;
    return decryptUnderMasterKey({ envelope, userMasterKey: key });
  }

  return {
    sealMediaEditorDoc: (docId, plaintext) => seal(DOC_RECORD_TYPE, docId, plaintext),
    unsealMediaEditorDoc: (docId, envelope) => unseal(DOC_RECORD_TYPE, docId, envelope),
    sealMediaEditorAsset: (assetId, data) => seal(ASSET_RECORD_TYPE, assetId, data),
    unsealMediaEditorAsset: (assetId, envelope) => unseal(ASSET_RECORD_TYPE, assetId, envelope),
    sealAudioProjectDoc: (docId, plaintext) => seal(AUDIOPROJ_RECORD_TYPE, docId, plaintext),
    unsealAudioProjectDoc: (docId, envelope) => unseal(AUDIOPROJ_RECORD_TYPE, docId, envelope),
    sealWebsiteSiteDoc: (docId, plaintext) => seal(SITE_DOC_RECORD_TYPE, docId, plaintext),
    unsealWebsiteSiteDoc: (docId, envelope) => unseal(SITE_DOC_RECORD_TYPE, docId, envelope),
  };
}

module.exports = {
  createMediaEditorSealAgent, DOC_RECORD_TYPE, ASSET_RECORD_TYPE, SITE_DOC_RECORD_TYPE,
  AUDIOPROJ_RECORD_TYPE,
};
